// services/tokenService.ts - FIXED VERSION
import { makeAPIRequest } from "../hook/request";
import { getClinicUserId } from "../utils/eira/helper";

interface TokenResponse {
  success: boolean;
  data: {
    token: string;
    user: {
      uid: string;
      email: string | null;
      phone: string;
      emailVerified: boolean;
    };
    expiresIn: number;
    expiresAt: string;
    tokenType: string;
  };
  timestamp: string;
}

interface RefreshResponse {
  success: boolean;
  data: {
    token: string;
    expiresAt: string;
    tokenType: string;
  };
}

interface StoredTokenData {
  token: string;
  expiresAt: string;
  user: {
    uid: string;
    email: string | null;
    phone: string;
    emailVerified: boolean;
  };
  tokenType: string;
}

class TokenService {
  private readonly DB_NAME = "EiraAuthDB";
  private readonly DB_VERSION = 1;
  private readonly STORE_NAME = "tokens";
  private readonly TOKEN_KEY = "auth_token";
  private readonly REFRESH_THRESHOLD_MINUTES = 4;
  private dbPromise: Promise<IDBDatabase> | null = null;

  constructor() {
    this.initDB();
  }

  private async initDB(): Promise<IDBDatabase> {
    if (this.dbPromise) {
      return this.dbPromise;
    }

    this.dbPromise = new Promise((resolve, reject) => {
      const request = indexedDB.open(this.DB_NAME, this.DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(this.STORE_NAME)) {
          db.createObjectStore(this.STORE_NAME, { keyPath: "id" });
        }
      };
    });

    return this.dbPromise;
  }

  async exchangeFirebaseToken(firebaseIdToken: string): Promise<TokenResponse> {
    try {
      const response = await makeAPIRequest({
        method: "POST",
        url: "https://madmachines-auth-layer-prod.dataorc.workers.dev/auth/verify",
        data: { firebaseToken: firebaseIdToken },
        headers: { "Content-Type": "application/json" },
      });

      // Handle the response properly - the API response might be nested
      const tokenResponse = response.data;

      if (tokenResponse && tokenResponse.success) {
        await this.storeTokenData(tokenResponse);
      } else {
        console.error(
          " TokenService: Token exchange failed - success was false or missing"
        );
        console.error(" TokenService: Full response:", tokenResponse);
        throw new Error("Token exchange failed - invalid response from server");
      }

      return tokenResponse;
    } catch (error) {
      console.error(" TokenService: exchangeFirebaseToken error:", error);
      throw error;
    }
  }

  async refreshToken(): Promise<RefreshResponse> {
    const currentToken = await this.getStoredToken();
    if (!currentToken) {
      throw new Error("No token available for refresh");
    }

    const response = await makeAPIRequest({
      method: "POST",
      url: "https://madmachines-auth-layer-prod.dataorc.workers.dev/auth/refresh",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${currentToken.token}`,
      },
    });

    if (response.data && response.data.success) {
      const updatedTokenData: StoredTokenData = {
        ...currentToken,
        token: response.data.data.token,
        expiresAt: response.data.data.expiresAt,
      };
      await this.saveToIndexedDB(updatedTokenData);
    }

    return response.data;
  }

  async storeTokenData(tokenResponse: TokenResponse): Promise<void> {
    try {
      const tokenData: StoredTokenData = {
        token: tokenResponse.data.token,
        expiresAt: tokenResponse.data.expiresAt,
        user: tokenResponse.data.user,
        tokenType: tokenResponse.data.tokenType,
      };

      // Validate required fields
      if (!tokenData.token || !tokenData.expiresAt || !tokenData.user) {
        console.error(
          " TokenService: Invalid token data - missing required fields"
        );
        throw new Error("Invalid token data received from server");
      }

      await this.saveToIndexedDB(tokenData);
    } catch (error) {
      console.error(" TokenService: storeTokenData error:", error);
      throw error;
    }
  }

  private async saveToIndexedDB(tokenData: StoredTokenData): Promise<void> {
    try {
      const db = await this.initDB();

      return new Promise((resolve, reject) => {
        const transaction = db.transaction([this.STORE_NAME], "readwrite");
        const store = transaction.objectStore(this.STORE_NAME);

        const dataToStore = {
          id: this.TOKEN_KEY,
          ...tokenData,
          savedAt: new Date().toISOString(),
        };

        const request = store.put(dataToStore);

        request.onsuccess = () => {
          resolve();
        };

        request.onerror = () => {
          console.error(" TokenService: IndexedDB save failed:", request.error);
          reject(request.error);
        };

        transaction.onerror = () => {
          console.error(
            " TokenService: IndexedDB transaction failed:",
            transaction.error
          );
          reject(transaction.error);
        };
      });
    } catch (error) {
      console.error(" TokenService: saveToIndexedDB error:", error);
      throw error;
    }
  }

  async getStoredToken(): Promise<StoredTokenData | null> {
    try {
      const db = await this.initDB();
      return new Promise((resolve) => {
        const transaction = db.transaction([this.STORE_NAME], "readonly");
        const store = transaction.objectStore(this.STORE_NAME);
        const request = store.get(this.TOKEN_KEY);

        request.onsuccess = () => {
          const result = request.result;
          if (result) {
            resolve({
              token: result.token,
              expiresAt: result.expiresAt,
              user: result.user,
              tokenType: result.tokenType,
            });
          } else {
            resolve(null);
          }
        };

        request.onerror = () => {
          console.error(
            " TokenService: Error retrieving token:",
            request.error
          );
          resolve(null);
        };
      });
    } catch (error) {
      console.error(" TokenService: getStoredToken error:", error);
      return null;
    }
  }

  async getToken(): Promise<string | null> {
    const stored = await this.getStoredToken();
    if (!stored || this.isTokenExpired(stored.expiresAt)) {
      return null;
    }
    return stored.token;
  }

  async getCurrentToken(): Promise<string | null> {
    const stored = await this.getStoredToken();
    if (!stored) {
      return null;
    }

    if (this.isTokenExpired(stored.expiresAt)) {
      console.warn(" TokenService: Token is expired, attempting refresh");
      try {
        const refreshResponse = await this.refreshToken();
        return refreshResponse.success ? refreshResponse.data.token : null;
      } catch (error) {
        console.error(" TokenService: Token refresh failed for expired token:", error);
        await this.clearStoredToken();
        return null;
      }
    }

    if (this.isTokenExpiringSoon(stored.expiresAt)) {
      try {
        console.log(" TokenService: Token expiring soon, refreshing proactively");
        const refreshResponse = await this.refreshToken();
        return refreshResponse.success ? refreshResponse.data.token : stored.token;
      } catch (error) {
        console.error(" TokenService: Proactive token refresh failed:", error);
        // Return current token if refresh fails, let the API call handle it
        return stored.token;
      }
    }

    return stored.token;
  }

  async isTokenValid(): Promise<boolean> {
    const stored = await this.getStoredToken();
    const isValid = stored ? !this.isTokenExpired(stored.expiresAt) : false;
    return isValid;
  }

  private isTokenExpired(expiresAt: string): boolean {
    const isExpired = new Date() >= new Date(expiresAt);
    if (isExpired) {
    }
    return isExpired;
  }

  private isTokenExpiringSoon(expiresAt: string): boolean {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const threshold = new Date(
      now.getTime() + this.REFRESH_THRESHOLD_MINUTES * 60 * 1000
    );
    const expiringSoon = expiry <= threshold;
    if (expiringSoon) {
    }
    return expiringSoon;
  }

  async getStoredUser(): Promise<StoredTokenData["user"] | null> {
    const stored = await this.getStoredToken();
    return stored?.user || null;
  }

  async getClinicUserId(): Promise<string | null> {
    const user = await this.getStoredUser();
    if (!user?.phone) return null;

    return getClinicUserId(user.phone);
  }

  async clearStoredToken(): Promise<void> {
    try {
      const db = await this.initDB();
      return new Promise((resolve) => {
        const transaction = db.transaction([this.STORE_NAME], "readwrite");
        const store = transaction.objectStore(this.STORE_NAME);
        const request = store.delete(this.TOKEN_KEY);

        request.onsuccess = () => {
          resolve();
        };

        request.onerror = () => {
          console.error(" TokenService: Error clearing token:", request.error);
          resolve();
        };
      });
    } catch (error) {
      console.error(" TokenService: clearStoredToken error:", error);
    }
  }

  getTargetServiceHeader(url: string): string {
    return url.includes("eira-app.dxai.life") ||
      url.includes("eira-dev.wemotive.in")
      ? "nodejs"
      : "strapi";
  }
}

export default new TokenService();
